<template>
	<v-dialog :value="value" max-width="460" @input="$emit('input', $event)">
		<v-card class="td-card">
			<div class="td-head">
				<v-icon color="primary" class="mr-2">mdi-image-size-select-large</v-icon>
				<div>
					<div class="td-title">{{ modifica ? 'Modifica miniatura' : 'Nuova miniatura' }}</div>
					<div class="td-sub">Lato in pixel delle immagini ridotte generate a ogni caricamento</div>
				</div>
				<v-spacer></v-spacer>
				<v-btn icon small title="Chiudi" @click="chiudi"><v-icon>mdi-close</v-icon></v-btn>
			</div>

			<v-form class="td-body" @submit.prevent="salva">
				<div class="td-row">
					<div class="td-fields">
						<v-text-field
							ref="campo"
							v-model="misura"
							type="number"
							min="1"
							max="5000"
							label="Misura"
							suffix="px"
							outlined
							class="td-input"
							:error-messages="errore"
							@input="errore = ''"
						></v-text-field>

						<div class="td-section">Misure comuni</div>
						<div class="td-chips">
							<v-chip
								v-for="c in comuni"
								:key="c"
								small
								:outlined="+misura !== c"
								:color="esiste(c) ? 'grey' : 'primary'"
								:disabled="esiste(c)"
								:title="esiste(c) ? 'Già presente' : ''"
								@click="scegli(c)"
							>
								{{ c }} px
							</v-chip>
						</div>
					</div>

					<!-- anteprima: quadrati in scala, la nuova misura evidenziata -->
					<div class="td-preview" title="Confronto con le miniature esistenti">
						<div v-for="q in quadrati" :key="q.chiave" class="td-square" :class="{ 'td-square--new': q.nuova }" :style="{ width: q.lato + 'px', height: q.lato + 'px' }">
							<span v-if="q.nuova" class="td-square-label">{{ q.size }}</span>
						</div>
					</div>
				</div>
				<div v-if="modifica" class="td-note">
					Le immagini già caricate conservano le miniature generate con la misura vecchia: quella nuova vale per i caricamenti successivi.
				</div>
			</v-form>

			<div class="td-foot">
				<v-btn text color="primary" @click="chiudi">Annulla</v-btn>
				<v-btn depressed color="primary" @click="salva">{{ modifica ? 'Salva' : 'Aggiungi' }}</v-btn>
			</div>
		</v-card>
	</v-dialog>
</template>

<script>
const LATO_MAX = 150;

// dialog di creazione/modifica di una miniatura: valida e restituisce la misura, il salvataggio lo fa la pagina
export default {
	name: 'ThumbnailDialog',
	props: {
		value: { type: Boolean, default: false },
		// misure gia' presenti (in modifica compresa quella che si sta cambiando)
		esistenti: { type: Array, default: () => [] },
		// misura in modifica, null per una nuova
		modifica: { type: Number, default: null },
	},
	data() {
		return {
			misura: '',
			errore: '',
			comuni: [150, 300, 600, 800, 1200],
		};
	},
	computed: {
		// le altre misure: in modifica quella di partenza non conta come doppione
		altre() {
			return this.esistenti.map(Number).filter(v => v !== this.modifica);
		},
		numero() {
			const n = Number(String(this.misura).replace(',', '.'));
			return Number.isFinite(n) && n > 0 ? n : 0;
		},
		quadrati() {
			const tutte = [...this.altre.map(size => ({ size, nuova: false }))];
			if (this.numero) tutte.push({ size: this.numero, nuova: true });
			const max = Math.max(...tutte.map(t => t.size), 1);
			// dal piu' grande al piu' piccolo: i piccoli restano sopra e visibili
			return tutte
				.sort((a, b) => b.size - a.size)
				.map((t, i) => ({ ...t, chiave: `${t.size}-${t.nuova}-${i}`, lato: Math.max(Math.round((t.size / max) * LATO_MAX), 6) }));
		},
	},
	watch: {
		value(aperto) {
			if (!aperto) return;
			this.misura = this.modifica ? String(this.modifica) : '';
			this.errore = '';
			this.$nextTick(() => setTimeout(() => this.$refs.campo && this.$refs.campo.focus(), 150));
		},
	},
	methods: {
		esiste(n) {
			return this.altre.includes(+n);
		},
		scegli(n) {
			this.misura = String(n);
			this.errore = '';
		},
		salva() {
			const testo = String(this.misura).trim();
			const n = Number(testo.replace(',', '.'));
			if (testo === '' || !Number.isFinite(n)) {
				this.errore = 'Scrivi la misura in pixel';
				return;
			}
			if (!Number.isInteger(n) || n < 1 || n > 5000) {
				this.errore = 'La misura deve essere un numero intero tra 1 e 5000';
				return;
			}
			if (this.esiste(n)) {
				this.errore = `La miniatura da ${n} px esiste già`;
				return;
			}
			this.$emit('save', n);
		},
		chiudi() {
			this.$emit('input', false);
		},
	},
};
</script>

<style scoped>
.td-card {
	border-radius: 10px !important;
	overflow: hidden;
}
.td-head {
	display: flex;
	align-items: center;
	padding: 14px 16px;
	border-bottom: 1px solid #e1e7ef;
}
.td-title {
	font-weight: 600;
	font-size: 16px;
	color: #1f2d3d;
}
.td-sub {
	font-size: 12px;
	color: #5f6b7a;
}
.td-body {
	padding: 16px 16px 8px;
	background: #f3f6fa;
}
.td-row {
	display: flex;
	gap: 16px;
	align-items: flex-start;
	flex-wrap: wrap;
}
.td-fields {
	flex: 1 1 200px;
	min-width: 0;
}
.td-input ::v-deep input {
	font-size: 22px;
	font-weight: 600;
}
.td-input ::v-deep input::-webkit-outer-spin-button,
.td-input ::v-deep input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
.td-input ::v-deep input[type='number'] {
	-moz-appearance: textfield;
}
.td-input ::v-deep .v-input__slot {
	background: #fff !important;
}
.td-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
	margin-bottom: 6px;
}
.td-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 12px;
}
.td-preview {
	position: relative;
	flex: 0 0 160px;
	width: 160px;
	height: 160px;
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	overflow: hidden;
}
.td-square {
	position: absolute;
	left: 5px;
	bottom: 5px;
	border: 1px solid #b8c4d4;
	background: rgba(184, 196, 212, 0.15);
	border-radius: 2px;
}
.td-square--new {
	border: 2px solid #1565c0;
	background: rgba(21, 101, 192, 0.15);
	z-index: 1;
}
.td-square-label {
	position: absolute;
	top: 2px;
	right: 4px;
	font-size: 10px;
	font-weight: 700;
	color: #1565c0;
}
.td-note {
	font-size: 12px;
	color: #5f6b7a;
	margin-bottom: 8px;
}
.td-foot {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 10px 16px;
	border-top: 1px solid #e1e7ef;
}
</style>
